import type { StaffAction, StaffRole, StaffStatus } from '../api/api.client';

/**
 * Vocabulaire des rôles et lecture des droits reçus du serveur.
 *
 * Aucune règle n'est décidée ici : la matrice vit côté serveur, qui l'applique et
 * renvoie la liste des actions permises. L'interface s'y réfère pour masquer ce
 * qui n'est pas permis — masquer un bouton n'est qu'une politesse, ce n'est pas
 * ce qui protège quoi que ce soit.
 */

export interface RoleDescription {
  readonly id: StaffRole;
  readonly label: string;
  /** Ce que le rôle permet, en une phrase, telle qu'elle s'affiche au moment d'inviter. */
  readonly summary: string;
}

export const STAFF_ROLE_DESCRIPTIONS: readonly RoleDescription[] = [
  {
    id: 'producteur',
    label: 'Co-titulaire',
    summary:
      'Tous les droits : gamme, points de retrait, commandes, équipe, informations légales et bancaires.',
  },
  {
    id: 'vendeur',
    label: 'Vendeur',
    summary:
      'Tient la gamme, les points de retrait et les commandes. Ne voit ni le SIRET, ni le compte bancaire, ni l’équipe.',
  },
  {
    id: 'staff',
    label: 'Renfort',
    summary:
      'Voit les commandes et confirme les retraits. Ne modifie ni la gamme ni les points de retrait.',
  },
];

export function staffRoleLabel(role: StaffRole): string {
  return STAFF_ROLE_DESCRIPTIONS.find((entry) => entry.id === role)?.label ?? role;
}

export function staffRoleSummary(role: StaffRole): string {
  return STAFF_ROLE_DESCRIPTIONS.find((entry) => entry.id === role)?.summary ?? '';
}

export function staffStatusLabel(status: StaffStatus): string {
  switch (status) {
    case 'invited':
      return 'Invitation en attente';
    case 'active':
      return 'Actif';
    case 'revoked':
      return 'Accès retiré';
  }
}

/**
 * L'action figure-t-elle dans les droits reçus ?
 *
 * On interroge la liste renvoyée par le serveur plutôt que de rejouer la matrice
 * ici : deux copies de la même règle finissent toujours par diverger, et la
 * divergence se voit d'abord sur un écran qui propose une action que l'API
 * refusera.
 */
export function allows(actions: readonly StaffAction[] | undefined, action: StaffAction): boolean {
  return actions !== undefined && actions.includes(action);
}
